const editableOpportunityFields = [
  'company', 'customerId', 'contactName', 'contactEmail', 'contactPhone', 'arrPotential',
  'probability', 'expectedCloseDate', 'nextAction', 'nextActionAt', 'budgetSent',
  'leadSource', 'industry', 'forecastCategory', 'kind', 'pricingSource',
  'pricingReference', 'salesOwnerId',
];

export const editableOpportunityPayload = row => {
  const defaults = {
    company: '',
    customerId: null,
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    arrPotential: 0,
    probability: 0,
    expectedCloseDate: null,
    nextAction: '',
    nextActionAt: null,
    budgetSent: false,
    leadSource: null,
    industry: null,
    forecastCategory: 'PIPELINE',
    kind: 'NEW',
    pricingSource: 'BCOME_PRICING_2026',
    pricingReference: '',
    salesOwnerId: null,
  };
  const result = Object.fromEntries(editableOpportunityFields.map(field => [
    field,
    row[field] === undefined ? defaults[field] : row[field],
  ]));
  result.arrPotential = Number(result.arrPotential || 0);
  result.probability = Number(result.probability || 0);
  result.customerId = result.customerId || null;
  result.expectedCloseDate = result.expectedCloseDate || null;
  result.nextActionAt = result.nextActionAt || null;
  result.salesOwnerId = result.salesOwnerId || null;
  result.pricingReference = String(result.pricingReference || '').trim() || null;
  result.pricingSource = result.pricingReference
    ? String(result.pricingSource || 'BCOME_PRICING_2026').trim()
    : null;
  result.featureIds = Array.isArray(row.featureIds) ? row.featureIds : [];
  return result;
};

export const pricingSnapshotPayload = row => ({
  source: String(row.pricingSource || 'BCOME_PRICING_2026').trim(),
  reference: String(row.pricingReference || '').trim(),
  version: String(row.pricingVersion || '').trim(),
  quotedAt: row.pricingQuotedAt,
  currency: String(row.pricingCurrency || 'EUR').trim().toUpperCase(),
  quotedAmount: Number(row.arrPotential || 0),
  services: Array.isArray(row.pricingServices) ? row.pricingServices : [],
  lines: JSON.parse(row.pricingLines || '[]'),
  discounts: JSON.parse(row.pricingDiscounts || '[]'),
  specialTerms: String(row.pricingSpecialTerms || '').trim() || null,
});

const searchText = row => [
  row.company,row.owner,row.contactName,row.contactEmail,row.pricingReference,
  ...(row.features || []).map(feature => feature.name),
].join(' ').toLocaleLowerCase();

export const filterPipelineBoard = (rows = [], filters = {}) => rows.filter(row => {
  const search=String(filters.search||'').trim().toLocaleLowerCase();
  if(search&&!searchText(row).includes(search))return false;
  if(filters.ownerId&&filters.ownerId!=='ALL'&&row.salesOwnerId!==filters.ownerId)return false;
  if(filters.kind&&filters.kind!=='ALL'&&row.kind!==filters.kind)return false;
  return true;
});

export const lostTransitionPayload = (stage, form) => ({
  stage,
  lossReasonCode:form.lossReasonCode,
  lossReasonNotes:String(form.lossReasonNotes||'').trim()||undefined,
  recontactAt:form.recontactAt||undefined,
});

export const archiveOpportunityPayload = reason => ({reason:String(reason||'').trim()});
export const groupPipelineByStage = (rows, stages) => stages.map(stage => ({
  ...stage,
  rows: rows.filter(row => row.stage === stage.code),
}));

export const pipelineStageValueLabel = stage => {
  const outcome = String(stage?.stageOutcome ?? stage?.outcome ?? '').toUpperCase();
  if (outcome === 'WON') return 'closed-won value';
  if (outcome === 'LOST') return 'closed-lost value';
  return 'open pipeline value';
};

export const contactRecencyLabel = row => {
  if (row.daysSinceLastContact === null || row.daysSinceLastContact === undefined) {
    return 'No client contact recorded';
  }
  if (row.daysSinceLastContact === 0) return 'Contacted today';
  if (row.daysSinceLastContact === 1) return 'Last contact yesterday';
  return `Last contact ${row.daysSinceLastContact} days ago`;
};

const configuredOutcome = row => String(row?.stageOutcome ?? row?.outcome ?? '').toUpperCase();

export const opportunityOutcomeLabel = row => {
  const outcome = configuredOutcome(row);
  return ['WON', 'LOST', 'ARCHIVED'].includes(outcome) ? outcome : 'CLOSED';
};

export const canProgressOpportunity = row => !['WON', 'LOST'].includes(configuredOutcome(row));

export const opportunityPrimaryAction = (row, onboardingMode = 'MANUAL') => {
  const outcome = configuredOutcome(row);
  if (row.customerId && outcome === 'WON') return { type: 'OPEN_CUSTOMER', label: 'Open customer' };
  if (!row.contactName || !row.contactEmail) return { type: 'EDIT_DETAILS', label: 'Add contact details' };
  if (outcome === 'WON') {
    return {
      type: 'ONBOARD',
      label: onboardingMode === 'AUTOMATIC' ? 'Review onboarding' : 'Start onboarding',
    };
  }
  if (!row.nextAction || !row.nextActionAt) return { type: 'PLAN_NEXT_STEP', label: 'Plan next step' };
  return { type: 'MOVE_STAGE', label: 'Move stage' };
};

export const canLogOpportunityInteraction = row => Boolean(row.contactName && row.contactEmail);

export const opportunitySecondaryActions = (row, primaryType) => {
  const actions = [];
  const open = canProgressOpportunity(row);
  if (primaryType !== 'EDIT_DETAILS' && (open || !row.customerId)) {
    actions.push({ type: 'EDIT_DETAILS', label: 'Edit details' });
  }
  if (primaryType !== 'PLAN_NEXT_STEP' && open && canLogOpportunityInteraction(row)) {
    actions.push({ type: 'PLAN_NEXT_STEP', label: row.nextAction ? 'Edit next step' : 'Plan next step' });
  }
  if (primaryType !== 'LOG_CONTACT' && open && canLogOpportunityInteraction(row)) {
    actions.push({ type: 'LOG_CONTACT', label: 'Log client contact' });
  }
  if (primaryType !== 'MOVE_STAGE' && open) {
    actions.push({ type: 'MOVE_STAGE', label: 'Move stage' });
  }
  if (primaryType !== 'OPEN_CUSTOMER' && row.customerId) {
    actions.push({ type: 'OPEN_CUSTOMER', label: 'Open customer' });
  }
  if (open) actions.push({ type: 'ARCHIVE', label: 'Archive' });
  return actions;
};

export const opportunityTransitionImpact = (stage, onboardingMode = 'MANUAL') => {
  const outcome = configuredOutcome(stage);
  const stageLabel = stage?.name || String(stage?.code || '').replaceAll('_', ' ');
  if (outcome === 'WON') {
    const automatic = onboardingMode === 'AUTOMATIC';
    return {
      title: automatic ? 'Win opportunity and onboard customer' : 'Win opportunity',
      detail: automatic
        ? `The stage changes to ${stageLabel} and onboarding starts immediately. The saved automation policy controls account creation, users, features and welcome messages.`
        : `The stage changes to ${stageLabel}, then this page opens the onboarding review. No customer, account, user or email is created until you confirm it.`,
      requiresLossReason: false,
      runsOnboarding: automatic,
    };
  }
  if (outcome === 'LOST') {
    return {
      title: 'Close opportunity as lost',
      detail: `The stage changes to ${stageLabel} and its loss reason is recorded. It remains visible on the board until Sales chooses Hide. No onboarding runs.`,
      requiresLossReason: true,
      runsOnboarding: false,
    };
  }
  return {
    title: `Move opportunity to ${stageLabel}`,
    detail: 'Only the pipeline stage and its probability change. No customer, user or email is created.',
    requiresLossReason: false,
    runsOnboarding: false,
  };
};

export const opportunityTransitionFollowUp = (stage, transition = {}) => {
  if (configuredOutcome(stage) !== 'WON') return { type: 'NONE' };
  const onboarding = transition.onboarding;
  if (!onboarding) return { type: 'NONE' };
  if (['MANUAL_ACTION_REQUIRED', 'NEEDS_INPUT'].includes(onboarding.status)) {
    return { type: 'OPEN_SETUP', reason: onboarding.reason };
  }
  return { type: 'SHOW_RESULT', run: onboarding };
};

export const commercialActionErrorMessage = cause => {
  const rawMessage = cause?.message || String(cause || '').trim();
  if (!rawMessage) return 'Please try again.';
  try {
    const body = JSON.parse(rawMessage);
    if (Array.isArray(body.message)) return body.message.join(' ');
    return body.message || rawMessage;
  } catch (_) {
    return rawMessage;
  }
};

export const defaultOpportunitySort = { field: 'priority', direction: 'desc' };

const opportunitySortValue = (row, field) => {
  if (field === 'priority') return Number.isFinite(Number(row.priority?.score)) ? Number(row.priority.score) : null;
  if (field === 'company') return String(row.company || '').trim().toLocaleLowerCase();
  if (field === 'stage') return String(row.stage || '').trim().toLocaleLowerCase();
  if (field === 'potentialArr') return Number(row.value || 0);
  if (field === 'weightedValue') return Number(row.value || 0) * Number(row.probability || 0);
  if (field === 'nextActionAt') {
    const timestamp = row.nextActionAt ? Date.parse(row.nextActionAt) : NaN;
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  return null;
};

export const sortOpportunities = (rows, sort = defaultOpportunitySort) => (
  rows.map((row, index) => ({ row, index })).sort((left, right) => {
    const leftValue = opportunitySortValue(left.row, sort.field);
    const rightValue = opportunitySortValue(right.row, sort.field);
    const leftMissing = leftValue === null || leftValue === '';
    const rightMissing = rightValue === null || rightValue === '';
    if (leftMissing || rightMissing) {
      if (leftMissing && rightMissing) return left.index - right.index;
      return leftMissing ? 1 : -1;
    }
    const comparison = typeof leftValue === 'string'
      ? leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' })
      : leftValue - rightValue;
    if (comparison === 0) return left.index - right.index;
    return sort.direction === 'desc' ? -comparison : comparison;
  }).map(({ row }) => row)
);

export const nextOpportunitySort = (current, field) => {
  if (current.field === field) {
    return { field, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  const descendingFirst = ['priority', 'potentialArr', 'weightedValue', 'nextActionAt'].includes(field);
  return { field, direction: descendingFirst ? 'desc' : 'asc' };
};

export const closedOpportunityRows = (rows = [], filters = {}) => {
  const search = String(filters.search || '').trim().toLocaleLowerCase();
  const outcome = String(filters.outcome || 'ALL').toUpperCase();
  return rows.filter(row => {
    if (outcome !== 'ALL' && configuredOutcome(row) !== outcome) return false;
    if (!search) return true;
    return [row.company, row.owner, row.contactName, row.contactEmail, row.lossReason,row.archiveReason]
      .some(value => String(value || '').toLocaleLowerCase().includes(search));
  }).sort((left, right) => String(right.archivedAt||right.closedAt || '').localeCompare(String(left.archivedAt||left.closedAt || '')));
};
